<template>
  <div class="invoiceBreakdown">
    <div class="pageContent">
      <heading type="h1">Invoice Breakdown</heading>
      <spinner v-if="loading" />
      <div class="container" v-if="invoice">
        <div class="row">
          <div class="col-md-8 col-lg-8">
            <div class="row">
              <div class="col-md-4 col-lg-4">
                <img class="invoiceBreakdown__logo" :src="clientLogo"/>
              </div>
              <div class="col-md-8 col-lg-8">
                <ul class="invoiceBreakdown__list list-group">
                  <li class="list-group-item">Client: {{ clientInfo.name }}</li>
                  <li class="list-group-item">Date Period: {{ invoice.period_start.value | formatDate }} - {{ invoice.period_end.value | formatDate }}</li>
                  <li class="list-group-item">Number: {{ invoice.external_id }} </li>
                  <li class="list-group-item">Total Amount: <span class="invoiceBreakdown__total"><span v-html="currencySymbol(this.invoice.currency)" v-if="invoice" />{{ delimitNumbers(invoice.summary.total_to_be_invoiced) }}</span></li>
                  <li class="list-group-item">Status: <span class="invoiceBreakdown__status">{{ invoice.status }}</span></li>
                  <li class="list-group-item" v-if="invoice.download_url">Invoice File: <a :href="downloadUrl(invoice.download_url)" target="_blank"><i class="fa fa-file-pdf-o"></i> Download Invoice</a> </li>
                </ul>
              </div>
            </div>

           <div class="invoiceBreakdown__section">
              <heading type="h2">Charges</heading>
              <heading type="h3">Downloads</heading>
              <invoice-table v-if="downloads.length > 0" :tableData="downloads" :columnData="table.chargesColumns" :currency="invoice.currency"/>
              <span v-else>{{ chargesMessage('downloads') }}</span>
            </div>

             <div class="invoiceBreakdown__section">
              <heading type="h3">Video Downloads</heading>
              <invoice-table v-if="downloadsvideo.length > 0" :tableData="downloadsvideo" :columnData="table.chargesColumns" :currency="invoice.currency"/>
              <span v-else>{{ chargesMessage('video downloads') }}</span>
            </div>

            <div class="invoiceBreakdown__section">
              <heading type="h3">Renders</heading>
              <invoice-table v-if="renders.length > 0" :tableData="renders" :columnData="table.chargesColumns" :currency="invoice.currency"/>
              <span v-else>{{ chargesMessage('renders') }}</span>
            </div>

            <div class="invoiceBreakdown__section">
              <heading type="h3">Creations</heading>
              <invoice-table v-if="creations.length > 0" :tableData="creations" :columnData="table.chargesColumns" :currency="invoice.currency" />
              <span v-else>{{ chargesMessage('creations') }}</span>
            </div>
            <hr />
            <div class="invoiceBreakdown__section">
              <heading type="h3">AdTag Creations</heading>
              <invoice-table v-if="adtagcreations.length > 0" :tableData="adtagcreations" :columnData="table.chargesColumns" :currency="invoice.currency" />
              <span v-else>{{ chargesMessage('adtags created') }}</span>
            </div>

            <div class="invoiceBreakdown__section">
              <heading type="h3">Video AdTag Creations</heading>
              <invoice-table v-if="adtagcreationsvideo.length > 0" :tableData="adtagcreationsvideo" :columnData="table.chargesColumns" :currency="invoice.currency" />
              <span v-else>{{ chargesMessage('video adtags created') }}</span>
            </div>

             <div class="invoiceBreakdown__section">
              <heading type="h3">AdTag Refreshes</heading>
              <invoice-table v-if="adtagrefreshes.length > 0" :tableData="adtagrefreshes" :columnData="table.chargesColumns" :currency="invoice.currency" />
              <span v-else>{{ chargesMessage('adtags refreshed') }}</span>
            </div>
            <hr />

            <div class="invoiceBreakdown__section">
              <heading type="h2">Adjustments</heading>
              <invoice-table :tableData="invoice.summary.adjustments" :columnData="table.adjustmentsColumns" :edit="onLaunchModal" :isAdmin="isAdmin" :currency="invoice.currency">
              </invoice-table>
            </div>
            <div class="invoiceBreakdown__section">
              <ul class="text-right">
                <li class="list-group-item">Adjustments: <span v-html="currencySymbol(this.invoice.currency)"/>{{
                  delimitNumbers(invoice.summary.total_adjustments) }}
                </li>
                <li class="list-group-item">VAT: {{ invoice.vat_rate }}&#37;</li>
                <li class="list-group-item">Excluding VAT: <span v-html="currencySymbol(this.invoice.currency)"/>{{
                  delimitNumbers(invoice.summary.ex_vat_total) }}
                </li>
                <li class="list-group-item">Total Amount: <span class="invoiceBreakdown__total" v-if="invoice"><span v-html="currencySymbol(this.invoice.currency)"/>{{
                  delimitNumbers(invoice.summary.total_to_be_invoiced) }}</span>
                </li>
              </ul>
            </div>
          </div>
          <div class="col-md-4 col-lg-4">
            <div class="invoiceBreakdown__ctas" v-if="isAdmin">
              <upload-file name="PDF Upload" @input="onUploadFile" acceptedFileType="application/pdf,application/vnd.ms-excel" />
              <button-cta title="GenerateOverview" data-modalType="generateOverview" :onClick="onLaunchModal" class="btn btn-primary" id="generateButton">Generate Overview Figures</button-cta>
              <form-group>
                <dropdown :name="status.name" :options="status.options" :current="currentStatus" @input="onUpdateStatus"/>
              </form-group>
              <button-cta data-modalType="addAdjustment" data-modalField="Add" :onClick="onLaunchModal" class="btn btn-primary">Add Adjustment</button-cta>
              <button-cta title="AddInvoiceNumber" data-modalType="invoiceNumber" :onClick="onLaunchModal" class="btn btn-primary">Add Invoice Number</button-cta>
            </div>
          </div>
        </div>
      </div>
    </div>

    <portal to="globalModal">
      <modal-invoice-number :show="showModal.invoiceNumber" @close="toggleModal('invoiceNumber')" />
      <modal-generate-overview :show="showModal.generateOverview" @close="toggleModal('generateOverview')" />
      <modal-delete-adjustment :show="showModal.deleteAdjustment" :adjustmentId="modalId" @close="toggleModal('deleteAdjustment')" />
      <modal-add-adjustment :show="showModal.addAdjustment" :adjustmentId="modalId" @close="toggleModal('addAdjustment')" />
      <modal-edit-adjustment :show="showModal.editAdjustment" :adjustmentId="modalId" @close="toggleModal('editAdjustment')" />
    </portal>

  </div>
</template>

<script>
import Heading from '@/components/Heading/heading'
import ButtonCta from '@/components/Button/button'
import InvoiceTable from '@/components/InvoiceBreakdownTable/invoice-breakdown-table'
import FormGroup from '@/components/Form/FormGroup/form-group'
import Dropdown from '@/components/Form/Dropdown/dropdown'
import {invoiceMixin} from '@/mixins/invoiceMixin'
import {modalMixin} from '@/mixins/modalMixin'
import {notificationMixin} from '@/mixins/notificationMixin'
import Spinner from '@/components/SpinnerIcon/spinner'
import UploadFile from '@/components/UploadFile/upload-file'
import Field from '@/components/Form/Field/field'
import ModalInvoiceNumber from '@/components/Modal/invoices/modal-invoice-number'
import ModalDeleteAdjustment from '@/components/Modal/invoices/modal-delete-adjustment'
import ModalAddAdjustment from '@/components/Modal/invoices/modal-add-adjustment'
import ModalEditAdjustment from '@/components/Modal/invoices/modal-edit-adjustment'
import ModalGenerateOverview from '@/components/Modal/invoices/modal-generate-overview'

import { API_URL } from '@/config'

export default {
  name: 'invoice_breakdown',
  components: {
    Spinner,
    Heading,
    ButtonCta,
    InvoiceTable,
    FormGroup,
    Dropdown,
    UploadFile,
    Field,
    ModalInvoiceNumber,
    ModalDeleteAdjustment,
    ModalAddAdjustment,
    ModalEditAdjustment,
    ModalGenerateOverview
  },
  props: {
    isAdmin: Boolean
  },
  mixins: [invoiceMixin, modalMixin, notificationMixin],
  data: () => ({
    table: {
      chargesColumns: {
        name: 'Project',
        campaign_job_number: 'Campaign Job Number',
        quantity: 'Quantity',
        amount: 'Cost'
      },
      adjustmentsColumns: {
        id: 'Id',
        type: 'Name',
        reason: 'Note',
        amount: 'Amount',
        date: 'Date',
        adjustmentsActions: ''
      }
    },
    status: {
      id: 'Status',
      name: 'Update Status',
      options: [
        {
          name: 'Active',
          value: 'active'
        },
        {
          name: 'Ready',
          value: 'ready'
        },
        {
          name: 'Invoiced',
          value: 'invoiced'
        },
        {
          name: 'Paid',
          value: 'paid'
        }
      ]
    }
  }),
  computed: {
    downloads () {
      return this.invoice.summary.charges.downloads.campaigns
    },
    downloadsvideo () {
      return this.invoice.summary.charges.downloadsvideo.campaigns
    },
    renders () {
      return this.invoice.summary.charges.renders.campaigns
    },
    creations () {
      return this.invoice.summary.charges.creations.campaigns
    },
    adtagcreations () {
      return this.invoice.summary.charges.adtagcreations.campaigns
    },
    adtagcreationsvideo () {
      return this.invoice.summary.charges.adtagcreationsvideo.campaigns
    },
    adtagrefreshes () {
      return this.invoice.summary.charges.adtagrefreshes.campaigns
    },
    currentStatus () {
      return this.invoice.status
    },
    clientInfo () {
      return this.invoice.client
    },
    clientLogo () {
      return API_URL + this.clientInfo.logo.substring(1)
    }
  },
  methods: {
    onUpdateStatus (status) {
      const statusData = {
        invoiceId: this.invoiceNumber,
        status
      }
      this.startLoading()
      this.updateInvoice(statusData).then(() => {
        this.getSingleInvoice(this.invoiceNumber).then(() => {
          this.addNotification('success', 'Invoice status updated.')
          this.finishLoading()
        }, error => {
          this.finishLoading()
          this.addNotification('danger', error)
        })
      }, error => {
        this.finishLoading()
        this.addNotification('danger', error)
      })
    },
    onUploadFile (e) {
      this.startLoading()
      const files = e.target.files || e.dataTransfer.files
      const formData = new FormData()
      formData.append('file', files[0])
      const data = {
        id: this.invoiceNumber,
        file: formData
      }
      this.uploadInvoiceFile(data).then(() => {
        this.getSingleInvoice(this.invoiceNumber).then(() => {
          this.addNotification('success', 'File successfully uploaded.')
          this.finishLoading()
        }, error => {
          this.finishLoading()
          this.addNotification('danger', error)
        })
      }, error => {
        this.finishLoading()
        this.addNotification('danger', error)
      })
    },
    chargesMessage (type) {
      return `No ${type} charges.`
    }
  },
  mounted () {},
  created () {
    this.startLoading()
    this.getSingleInvoice(this.invoiceNumber).then(() => {
      this.finishLoading()
    }, error => {
      this.finishLoading()
      this.addNotification('danger', error)
    })
  }
}
</script>

<style lang="scss">
  .invoiceBreakdown {
    &__logo {
      max-width: 200px;
    }
    &__list {
      list-style: none;
      margin: 0 0 30px;
      padding: 0;
    }
    &__section {
      margin-bottom: 30px;
    }
    &__total {
      font-weight: bold;
    }
    &__status {
      text-transform: capitalize;
    }
    &__ctas
      #generateButton, .uploadFile{
        margin-bottom:15px;
      }
  }
</style>



// WEBPACK FOOTER //
// src/pages/invoice/invoice-breakdown.vue